"use client";

import { Link, Image, usePathname, useNavigate } from "@/src/lib/next-compat";
import { useClerk, useUser, ClerkLoaded, ClerkLoading, UserButton } from "@clerk/clerk-react";
import {
    LayoutDashboard,
    LogOut,
    X,
    ChevronUp,
    Sparkles,
    BadgeDollarSign,
    Award,
    Building2,
    RotateCcw,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useUserProfile } from "@/src/lib/hooks/useUserProfile";
import { startOnboardingTour } from "@/components/student/OnboardingTour";

interface SidebarProps {
    isOpen?: boolean;
    isCollapsed?: boolean;
    onClose?: () => void;
    onCoursesClick?: () => void;
}

export default function Sidebar({ isOpen = true, isCollapsed = false, onClose, onCoursesClick }: SidebarProps) {
    const pathname = usePathname();
    const navigate = useNavigate();
    const { signOut, openUserProfile } = useClerk();
    const { user: clerkUser } = useUser();
    const { data: userProfile } = useUserProfile();
    const displayedName = clerkUser?.firstName || "User";
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const profileMenuRef = useRef<HTMLDivElement>(null);

    const adminOrgs = (userProfile?.organizations || []).filter(
        (org) => !org.role || org.role.toLowerCase() === "admin" || org.role.toLowerCase() === "manager" || org.role.toLowerCase() === "superadmin"
    );

    useEffect(() => {
        if (!profileMenuOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
                setProfileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [profileMenuOpen]);

    const is_active = (path: string) => pathname === path;

    // The logo goes home for everyone. Pointing it at /courses while signed in left the
    // signed-in shell with no link to "/" at all, which is what made the landing page
    // unreachable. "Courses" is one row down in this same nav, so nothing is lost.
    const logoHref = "/";

    // Prevent body scroll when mobile/tablet menu is open
    useEffect(() => {
        if (isOpen && window.innerWidth < 1024) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleLinkClick = () => {
        // Close mobile menu when a link is clicked
        if (onClose && window.innerWidth < 1024) {
            onClose();
        }
    };

    const handleCoursesClick = (e: React.MouseEvent) => {
        handleLinkClick();
        if (onCoursesClick && pathname !== "/courses") {
            e.preventDefault();
            onCoursesClick();
        }
    };

    const hasOrgMembership = (userProfile?.organizations?.length ?? 0) > 0;

    const navSections = [
        ...(adminOrgs.length > 0
            ? [
                {
                    title: "Organization",
                    items: adminOrgs.map((org) => ({
                        name: `${org.name} Hub`,
                        href: `/org/${org.slug}/dashboard`,
                        icon: Building2,
                    })),
                },
            ]
            : []),
        {
            title: "Learn",
            items: [
                { name: "Courses", href: "/courses", icon: LayoutDashboard },
                { name: "Certificates", href: "/certificates", icon: Award },
            ],
        },
        {
            title: "Account",
            items: [
                { name: "Account", href: "/account", icon: Sparkles },
                ...(!hasOrgMembership
                    ? [{ name: "Pricing", href: "/account/pricing", icon: BadgeDollarSign }]
                    : []),
            ],
        },
    ];

    return (
        <>
            {/* Mobile/Tablet Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    bg-[#05192D] text-gray-300 flex flex-col h-screen border-r border-gray-800 flex-shrink-0
                    fixed lg:sticky top-0 z-50
                    transition-all duration-300 ease-in-out
                    ${isCollapsed
                        ? 'lg:-translate-x-full lg:w-0 lg:border-r-0 lg:overflow-hidden lg:opacity-0 pointer-events-none'
                        : 'lg:translate-x-0 lg:w-56 lg:opacity-100'}
                    ${isOpen ? 'translate-x-0 w-56' : '-translate-x-full'}
                `}
                aria-hidden={isCollapsed}
            >
                <div className="w-56 flex flex-col h-full min-w-[14rem]">
                {/* Mobile/Tablet Close Button */}
                <button
                    onClick={onClose}
                    className="lg:hidden absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
                    aria-label="Close menu"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Logo Area */}
                <div className="p-4 mb-2 flex justify-center">
                    <Link
                        to={logoHref}
                        className="flex items-center justify-center rounded-full bg-white px-4 py-2"
                        onClick={handleLinkClick}
                    >
                        <img                             src="/images/logo.png"
                            alt="inaraX"
                            width={100}
                            height={32}
                            className="h-7 w-auto object-contain"
                        />
                    </Link>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto px-3 space-y-6 no-scrollbar" data-tour="sidebar-nav">
                    {navSections.map((section) => (
                        <div key={section.title}>
                            <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2 px-3">
                                {section.title}
                            </h3>
                            <ul className="list-none ml-0 space-y-0.5">
                                {section.items.map((item) => {
                                    const active = is_active(item.href);
                                    const isCourses = item.href === "/courses";
                                    return (
                                        <li key={item.name}>
                                            <Link
                                                to={item.href}
                                                onClick={isCourses ? handleCoursesClick : handleLinkClick}
                                                className={`flex items-center justify-between px-3 py-3 lg:py-2 rounded-md text-sm font-medium transition-colors ${active
                                                    ? "bg-[#183145] text-white"
                                                    : "hover:bg-[#183145] hover:text-white"
                                                    }`}
                                                data-tour={isCourses ? "sidebar-courses" : undefined}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <item.icon className={`w-4 h-4 ${active ? 'text-white' : 'text-gray-400'}`} />
                                                    {item.name}
                                                </div>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </nav>

                {/* Footer / Profile + Submenu */}
                <div className="p-3 border-t border-gray-800 space-y-1 relative">
                    <ClerkLoading>
                        <div className="flex items-center gap-3 px-3 py-2">
                            <div className="h-8 w-8 rounded-full bg-gray-700" />
                            <div className="h-4 w-24 bg-gray-700 rounded" />
                        </div>
                    </ClerkLoading>
                    <ClerkLoaded>
                        <div className="relative" ref={profileMenuRef}>
                            <button
                                type="button"
                                onClick={() => setProfileMenuOpen((v) => !v)}
                                className="flex items-center gap-3 w-full px-2 py-2 rounded-md hover:bg-[#183145] transition-colors text-left"
                            >
                                <UserButton
                                    afterSignOutUrl="/"
                                    appearance={{
                                        elements: { avatarBox: "h-8 w-8 ring-0" },
                                    }}
                                />
                                <span className="flex-1 min-w-0 text-sm font-medium text-gray-300 truncate">
                                    {displayedName}
                                </span>
                                <ChevronUp className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${profileMenuOpen ? "rotate-0" : "rotate-180"}`} />
                            </button>
                            {profileMenuOpen && (
                                <div
                                    className="absolute left-0 bottom-full mb-1 w-56 rounded-lg bg-[#0f2435] border border-gray-700 shadow-xl z-[60] py-1 flex flex-col"
                                    role="menu"
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileMenuOpen(false);
                                            openUserProfile();
                                        }}
                                        className="flex items-center gap-3 w-full px-3 py-2.5 text-left text-sm text-gray-300 hover:bg-[#183145] hover:text-white transition-colors"
                                        role="menuitem"
                                    >
                                        <LayoutDashboard className="w-4 h-4 shrink-0" />
                                        Manage account
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileMenuOpen(false);
                                            startOnboardingTour(navigate);
                                        }}
                                        className="flex items-center gap-3 w-full px-3 py-2.5 text-left text-sm text-gray-300 hover:bg-[#183145] hover:text-white transition-colors"
                                        role="menuitem"
                                    >
                                        <RotateCcw className="w-4 h-4 shrink-0" />
                                        Replay Tour
                                    </button>
                                    <button
                                         type="button"
                                         onClick={() => {
                                             setProfileMenuOpen(false);
                                             void signOut().then(() => {
                                                 window.location.href = "/";
                                             });
                                         }}
                                         className="flex items-center gap-3 w-full px-3 py-2.5 text-left text-sm text-gray-300 hover:bg-[#183145] hover:text-white transition-colors"
                                         role="menuitem"
                                     >
                                        <LogOut className="w-4 h-4 shrink-0" />
                                        Sign out
                                    </button>
                                </div>
                            )}
                        </div>
                    </ClerkLoaded>
                </div>
                </div>
            </aside>
        </>
    );
}
